import { useState, useEffect, useRef, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useSupabase } from '@/hooks/useSupabase'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/hooks/useAuth'
import { showToast } from '@/components/layout/Toast'
import type { Cotizacion, Empresa, CotizacionItemsEstandar, CotizacionItemsEquipo, CotizacionItemsTareas, CotizacionTarea, EstadoCotizacion } from '@/lib/types'
import { formatCurrency, formatDate, getDiscountTier, fmtPct, processContent, fmtFTE, marginClass, sanitizeHtml, r2 } from '@/lib/utils'
import { expandirBloques } from '@/lib/cotizacion-bloques'
import TaskDetailPages from '@/components/cotizaciones/TaskDetailPages'
import { DatosBancarios } from '@/components/cotizaciones/DatosBancarios'
import { IconCopy, IconFileText } from '@/components/icons/Icons'
import { PageHeader, Breadcrumbs } from '@/components/ui'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

export default function VerCotizacion() {
  const { id } = useParams<{ id: string }>()
  const nav = useNavigate()
  const db = useSupabase()
  const api = useApi()
  const { token } = useAuth()
  const pdfRef = useRef<HTMLDivElement>(null)

  const [cot, setCot] = useState<Cotizacion | null>(null)
  const [empresa, setEmpresa] = useState<Empresa | null>(null)
  const [loading, setLoading] = useState(true)
  const [exportStep, setExportStep] = useState(-1)
  const cancelledRef = useRef(false)
  const [viewMode, setViewMode] = useState<'internal' | 'cliente'>('internal')
  const [sendingWA, setSendingWA] = useState(false)
  const [showWAModal, setShowWAModal] = useState(false)
  const [waPhone, setWaPhone] = useState('')
  const [showEstadoMenu, setShowEstadoMenu] = useState(false)
  const [savingEstado, setSavingEstado] = useState(false)
  const [duplicating, setDuplicating] = useState(false)
  /*
    Facturarle al partner lo que nos debe por esta cotización. Solo aparece
    cuando hay algo que cobrar: aceptada, de la cartera de un partner y con
    al menos un ítem que tenga `costo_partner`.
  */
  const [facturandoPartner, setFacturandoPartner] = useState(false)
  const estadoMenuRef = useRef<HTMLDivElement>(null)

  const estadoIcon = (d: string, color?: string) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color || 'currentColor'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={d}/></svg>
  )

  const estadoOptions: { value: EstadoCotizacion; label: string; icon: React.ReactNode }[] = [
    { value: 'borrador', label: 'Borrador', icon: estadoIcon('M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7 M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z', 'var(--text-secondary)') },
    { value: 'enviada', label: 'Enviada', icon: estadoIcon('M22 2L11 13 M22 2l-7 20-4-9-9-4 20-7z', 'var(--info)') },
    { value: 'en_negociacion', label: 'En Negociación', icon: estadoIcon('M17 11h1a3 3 0 0 1 0 6h-1 M7 11H6a3 3 0 0 0 0 6h1 M7 11V7a5 5 0 0 1 10 0v4 M8 17v1a3 3 0 0 0 3 3h2a3 3 0 0 0 3-3v-1', '#92400e') },
    { value: 'aceptada', label: 'Aceptada', icon: estadoIcon('M22 11.08V12a10 10 0 1 1-5.93-9.14 M22 4L12 14.01l-3-3', 'var(--success)') },
    { value: 'rechazada', label: 'Rechazada', icon: estadoIcon('M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z M15 9l-6 6 M9 9l6 6', 'var(--danger)') },
  ]

  // Close estado menu on outside click
  useEffect(() => {
    if (!showEstadoMenu) return
    const handler = (e: MouseEvent) => {
      if (estadoMenuRef.current && !estadoMenuRef.current.contains(e.target as Node)) {
        setShowEstadoMenu(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showEstadoMenu])

  const handleStatusChange = async (nuevoEstado: EstadoCotizacion) => {
    if (!cot || nuevoEstado === cot.estado) { setShowEstadoMenu(false); return }
    setSavingEstado(true)
    try {
      await db.update('cotizaciones', { id: cot.id, estado: nuevoEstado })
      setCot(prev => prev ? { ...prev, estado: nuevoEstado } : prev)
      showToast(`Estado cambiado a ${estadoOptions.find(o => o.value === nuevoEstado)?.label || nuevoEstado}`, 'success')
    } catch {
      showToast('Error al cambiar estado', 'error')
    } finally {
      setSavingEstado(false)
      setShowEstadoMenu(false)
    }
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const m = (await import('mermaid')).default
        if (!cancelled) m.initialize({ startOnLoad: false, theme: 'base', themeVariables: { primaryColor: '#4F7FE8', primaryBorderColor: '#1C4587', lineColor: '#1C4587', secondaryColor: '#eef1f8', tertiaryColor: '#f8f9fc' } })
      } catch (e) { console.warn('[mermaid] init error:', e) }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    let cancelled = false
    const safetyTimer = setTimeout(() => { if (!cancelled) setLoading(false) }, 15000)
    ;(async () => {
      try {
        const [emp, c] = await Promise.all([
          db.getAll<Empresa>('empresa'),
          db.getById<Cotizacion>('cotizaciones', parseInt(id!)),
        ])
        if (cancelled) return
        clearTimeout(safetyTimer)
        const empresaData = Array.isArray(emp) ? emp[0] || null : (emp && 'id' in emp ? emp : null)
        setEmpresa(empresaData as Empresa | null); setCot(c)
        if (!c) { showToast('Cotización no encontrada', 'error'); nav('/cotizaciones') }
      } catch { showToast('Error al cargar', 'error') }
      finally { if (!cancelled) setLoading(false) }
    })()
    return () => { cancelled = true; clearTimeout(safetyTimer) }
  }, [id])

  /** Export PDF via backend (Gotenberg + R2) */
  const exportPDF = async () => {
    if (!cot) return
    cancelledRef.current = false
    setExportStep(0)
    try {
      // Step 0: Generate PDF on backend (Gotenberg → R2)
      const result = await api.post<{ ok: boolean; url: string; version: number; r2Key: string; sizeBytes: number }>(
        `/api/pdf/generate/cotizacion/${cot.id}`, {}
      )
      if (!result.ok) throw new Error('No se pudo generar el PDF')
      if (cancelledRef.current) throw new Error('Exportación cancelada')

      // Step 1-2: Upload + DB update already handled by backend
      setExportStep(2)
      setCot(prev => prev ? { ...prev, pdf_url: result.url } : prev)

      // Step 3: Download via proxy endpoint
      setExportStep(3)
      const downloadUrl = `${API_URL}/api/pdf/download/cotizacion/${cot.id}?v=${result.version}`
      const res = await fetch(downloadUrl, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      })
      if (!res.ok) throw new Error('Error al descargar PDF')
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${cot.proyecto_nombre || 'Cotizacion'} - ${cot.cliente_nombre || cot.numero}.pdf`
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 1000)

      // Step 4: Done
      setExportStep(4)
      showToast('PDF descargado ✓', 'success')
      await new Promise(r => setTimeout(r, 600))
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'desconocido'
      if (msg !== 'Exportación cancelada') showToast('Error al exportar: ' + msg, 'error')
      else showToast('Exportación cancelada', 'info')
    } finally { setExportStep(-1) }
  }

  /** Send cotización via WhatsApp with PDF attachment */
  const sendWhatsApp = async () => {
    if (!cot || !waPhone.trim()) return
    setSendingWA(true)
    showToast('Generando PDF y enviando por WhatsApp…', 'info')
    try {
      // 1. Generate PDF on backend (or use existing pdf_url)
      let pdfUrl = cot.pdf_url
      if (!pdfUrl) {
        const pdfResult = await api.post<{ ok: boolean; url: string }>(
          `/api/pdf/generate/cotizacion/${cot.id}`, {}
        )
        if (!pdfResult.ok) throw new Error('No se pudo generar el PDF')
        pdfUrl = pdfResult.url
        setCot(prev => prev ? { ...prev, pdf_url: pdfUrl! } : prev)
      }

      // 2. Build portal link
      const portalUrl = cot.public_token
        ? `${window.location.origin}/ver/${cot.public_token}`
        : undefined

      // 3. Format phone (ensure country code, no +)
      let phone = waPhone.trim().replace(/[^0-9]/g, '')
      if (phone.startsWith('0')) phone = '51' + phone.slice(1)
      if (phone.length <= 9) phone = '51' + phone

      // 4. Build download URL for the PDF (proxied through API)
      const downloadUrl = `${API_URL}/api/pdf/download/cotizacion/${cot.id}`

      // 5. Call send-notification
      const res = await fetch(
        `${API_URL}/api/notifications/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            evento: 'cotizacion_enviada',
            variables: {
              nombre: cot.cliente_contacto || cot.cliente_nombre || 'Cliente',
              numero: cot.numero,
              monto: formatCurrency(cot.total, cot.moneda),
              url: portalUrl || downloadUrl,
            },
            telefono: phone,
            empresa_id: cot.cliente_id || null,
            contentUri: downloadUrl,
          }),
        }
      )

      const result = await res.json()
      if (!res.ok || !result.success) {
        throw new Error(result.error || 'Error al enviar notificación')
      }

      showToast('Cotización enviada por WhatsApp ✓', 'success')
      setShowWAModal(false)
      setWaPhone('')
    } catch (e) {
      showToast('Error: ' + (e instanceof Error ? e.message : 'desconocido'), 'error')
    } finally {
      setSendingWA(false)
    }
  }

  /** Duplicate this cotización as a new borrador */
  /** Lo que el partner nos debe por esta cotización: la suma de los costos. */
  const costoPartner = useMemo(() => {
    const items = Array.isArray(cot?.items) ? (cot!.items as CotizacionItemsEstandar[]) : []
    return r2(items.reduce(
      (acc, it) => acc + (Number(it?.costo_partner) || 0) * (Number(it?.cantidad) || 0),
      0,
    ))
  }, [cot])

  const facturarAlPartner = async () => {
    if (!cot) return
    setFacturandoPartner(true)
    try {
      const f = await api.post<{ id: number; numero: string }>(
        '/api/facturas/from-cotizacion-partner', { cotizacion_id: cot.id },
      )
      showToast(`Factura ${f.numero} creada en borrador`, 'success')
      nav(`/facturas/${f.id}`)
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'No se pudo generar la factura', 'error')
    } finally { setFacturandoPartner(false) }
  }

  const handleDuplicate = async () => {
    if (!cot) return
    setDuplicating(true)
    try {
      const { id: _id, numero: _num, public_token: _tok, pdf_url: _pdf, created_at: _ca, cotizacion_origen_id: _ori, tareas_seleccionadas: _ts, ...rest } = cot as Record<string, unknown> & Cotizacion
      const today = new Date().toISOString().slice(0, 10)
      const data: Record<string, unknown> = {
        ...rest,
        estado: 'borrador',
        fecha_emision: today,
        proyecto_nombre: cot.proyecto_nombre ? `${cot.proyecto_nombre} (Copia)` : null,
      }
      const newId = await db.add('cotizaciones', data)
      showToast('Cotización duplicada ✓', 'success')
      const tipo = cot.tipo_cotizacion
      const path = tipo === 'equipo_dedicado' ? `/equipo/${newId}` : tipo === 'tareas' ? `/tareas-cot/${newId}` : `/cotizaciones/${newId}`
      nav(path)
    } catch (e) {
      showToast('Error al duplicar: ' + (e instanceof Error ? e.message : 'desconocido'), 'error')
    } finally {
      setDuplicating(false)
    }
  }

  // Mermaid render — must be before the loading guard to keep hook count stable
  useEffect(() => {
    if (!cot) return
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const els = pdfRef.current?.querySelectorAll('.cot-mermaid-container .mermaid')
        if (!els || els.length === 0 || cancelled) return
        console.warn('[mermaid] rendering', els.length, 'diagrams')
        const m = (await import('mermaid')).default
        if (!cancelled) await m.run({ nodes: Array.from(els) as any })
        console.warn('[mermaid] render complete')
      } catch (e) { console.warn('[mermaid] render error:', e) }
    }, 100)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [cot])

  if (loading || !cot || !empresa) return <div className="page-enter" style={{ padding: '2rem' }}><PageHeader breadcrumbs={[{ label: 'Cotizaciones', to: '/cotizaciones' }, { label: 'Cotización' }]} /><div style={{ color: 'var(--text-secondary)', textAlign: 'center', marginTop: 40 }}><div className="auth-spinner" style={{ margin: '0 auto 12px' }} />Cargando...</div></div>

  const isEquipo = cot.tipo_cotizacion === 'equipo_dedicado'
  const items = (!isEquipo && Array.isArray(cot.items) ? cot.items : []) as CotizacionItemsEstandar[]
  const secciones = Array.isArray(cot.secciones) ? cot.secciones : []
  const proyectoNombre = cot.proyecto_nombre || items[0]?.descripcion || 'Propuesta de Servicios'
  const pc = empresa.color_primario || '#1C4587'
  // El white-label cuelga de `branding.nombre`, no de que `branding` exista:
  // ese objeto lleva también el modo de precio (`presupuesto_mas_igv`), que una
  // cotización propia de 1Conecta puede usar sin ser marca blanca. Con la
  // condición vieja, elegir "Monto + IGV" en una cotización propia borraba la
  // razón social, el RUC y los datos bancarios del documento.
  const brand = cot.branding ?? null
  const isBranded = !!brand?.nombre
  const dispNombre = brand?.nombre || empresa.nombre || '1Conecta'
  const dispWebsite = isBranded ? (brand?.website || '') : (empresa.website || '')
  const tier = getDiscountTier(cot.descuento_porcentaje)

  // Equipo data
  const equipoData = isEquipo && cot.items && typeof cot.items === 'object' && 'equipo' in cot.items ? cot.items as CotizacionItemsEquipo : null
  const equipoMembers = equipoData?.equipo ?? []
  const equipoTotales = equipoData?.totales

  // Tareas data
  const isTareas = cot.tipo_cotizacion === 'tareas'
  const tareasData = isTareas && cot.items && typeof cot.items === 'object' && 'tareas' in cot.items ? cot.items as CotizacionItemsTareas : null
  const tareasItems: CotizacionTarea[] = tareasData?.tareas ?? []

  const editPath = isEquipo ? `/equipo/${cot.id}/editar` : isTareas ? `/tareas-cot/${cot.id}/editar` : `/cotizaciones/${cot.id}/editar`

  /*
    Cotización cuyo documento es un ARCHIVO subido (la hizo un partner en su
    herramienta). Todo lo que sigue abajo renderiza el documento desde
    `items`/`secciones`, que acá están vacíos: saldría una cotización con la
    tabla sin filas y el total en cero.

    Es una pantalla aparte y no una rama adentro porque casi ninguna de las
    acciones de esta página aplica — "Editar" abriría el constructor sobre un
    documento que no se armó acá, y regenerar el PDF lo pisaría con uno vacío.
    Lo que sí aplica es cambiar el estado y pasar el enlace.
  */
  if (cot.documento_subido) {
    const archivo = `${API_URL}/api/pdf/public/cotizacion/${cot.public_token}/archivo`
    return (
      <div className="page-enter page-shell">
        <div className="page-header page-header--crumbs">
          <div>
            <Breadcrumbs items={[
              { label: 'Cotizaciones', to: '/cotizaciones' },
              { label: `#${cot.numero}` },
            ]} />
            <p className="page-subtitle">
              {cot.cliente_nombre || 'Sin cliente'} — documento subido
            </p>
          </div>
          <div className="page-actions">
            <select
              className="form-input"
              value={cot.estado}
              onChange={e => handleStatusChange(e.target.value as EstadoCotizacion)}
              disabled={savingEstado}
              style={{ width: 160 }}
            >
              {estadoOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            <button
              className="btn btn-secondary"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/ver/${cot.public_token}`)
                  .then(() => showToast('Enlace copiado', 'success'))
                  .catch(() => showToast('No se pudo copiar', 'error'))
              }}
            >
              Copiar enlace
            </button>
            <a className="btn btn-secondary" href={archivo} download={`cotizacion-${cot.numero}.pdf`}>
              Descargar PDF
            </a>
          </div>
        </div>
        <iframe
          className="portal-archivo__marco page-shell__table"
          src={archivo}
          title={`Cotización ${cot.numero}`}
        />
      </div>
    )
  }

  return (
    <div className="page-enter">
      <div className="page-header page-header--crumbs">
        <div>
          <Breadcrumbs items={[
            { label: 'Cotizaciones', to: '/cotizaciones' },
            { label: `#${cot.numero}${isEquipo ? ' — Equipo Dedicado' : isTareas ? ' — Propuesta por Tareas' : ''}` },
          ]} />
          <p className="page-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {cot.cliente_nombre || 'Sin cliente'} —
            <span className="estado-quick-edit" ref={estadoMenuRef}>
              <button
                className={`badge badge-${cot.estado} estado-quick-btn`}
                onClick={() => setShowEstadoMenu(v => !v)}
                disabled={savingEstado}
                title="Cambiar estado"
              >
                {savingEstado ? '...' : cot.estado === 'en_negociacion' ? 'en negociación' : cot.estado}
                <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" style={{ marginLeft: 4, opacity: 0.7 }}><path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </button>
              {showEstadoMenu && (
                <div className="estado-quick-menu">
                  {estadoOptions.map(opt => (
                    <button
                      key={opt.value}
                      className={`estado-quick-option${cot.estado === opt.value ? ' active' : ''}`}
                      onClick={() => handleStatusChange(opt.value)}
                    >
                      <span>{opt.icon}</span>
                      <span>{opt.label}</span>
                      {cot.estado === opt.value && <span style={{ marginLeft: 'auto', fontSize: '0.7rem' }}>●</span>}
                    </button>
                  ))}
                </div>
              )}
            </span>
          </p>
          {cot.cotizacion_origen_id && (
            <Link
              to={`/tareas-cot/${cot.cotizacion_origen_id}`}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.75rem', color: 'var(--primary)', marginTop: 4, textDecoration: 'none' }}
            >
              <IconFileText size={12} /> Generada desde Propuesta por Tareas
            </Link>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Link to={editPath} className="btn btn-secondary">Editar</Link>
          {/* El documento que faltaba: la cotización es del partner a SU
              cliente, y de ahí sale lo que él nos debe a nosotros. Sin costos
              cargados no hay nada que cobrar y el botón no aparece. */}
          {cot.estado === 'aceptada' && costoPartner > 0 && (
            <button
              className="btn btn-secondary"
              onClick={facturarAlPartner}
              disabled={facturandoPartner}
              title="Genera nuestra factura al partner por los servicios nuestros de esta cotización"
            >
              {facturandoPartner
                ? 'Generando…'
                : <>Facturar al partner · {formatCurrency(costoPartner, cot.moneda)}</>}
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={handleDuplicate}
            disabled={duplicating}
          >
            {duplicating ? 'Duplicando…' : <><IconCopy size={14} /> Duplicar</>}
          </button>
          <button className="btn btn-primary" onClick={exportPDF} disabled={exportStep >= 0}>
            {exportStep >= 0 ? `Exportando (${exportStep + 1}/5)` : 'Exportar PDF'}
          </button>
          <button
            className="btn"
            style={{ background: '#25D366', color: '#fff', border: 'none' }}
            onClick={() => { setWaPhone(cot.cliente_telefono || ''); setShowWAModal(true) }}
            disabled={sendingWA}
          >
            {sendingWA ? 'Enviando…' : '📱 Enviar WhatsApp'}
          </button>
        </div>
      </div>

      {/* Export Progress Modal */}
      {exportStep >= 0 && (
        <div className="modal-backdrop">
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 380 }}>
            <div className="modal-header">
              <h3>Exportando Cotización</h3>
            </div>
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              {exportStep < 4 && <div className="export-spinner" />}
              <div className="export-step-counter">{exportStep + 1}/5</div>
              <div className="export-step-label">
                {['Generando PDF…', 'Subiendo a Storage…', 'Guardando referencia…', 'Descargando…', 'Completado ✓'][exportStep]}
              </div>
              <div className="export-progress-bar">
                <div className="export-progress-fill" style={{ width: `${((exportStep + 1) / 5) * 100}%` }} />
              </div>
            </div>
            {exportStep < 4 && (
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => { cancelledRef.current = true }}>Cancelar</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* WhatsApp Send Modal */}
      {showWAModal && (
        <div className="modal-backdrop" onClick={() => !sendingWA && setShowWAModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <h3>Enviar Cotización por WhatsApp</h3>
              <button className="modal-close" onClick={() => !sendingWA && setShowWAModal(false)}>×</button>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <p style={{ marginBottom: 12, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                Se enviará la cotización <strong>{cot.numero}</strong> con el PDF adjunto al número indicado.
              </p>
              <label className="form-label">Teléfono (con código de país)</label>
              <input
                type="tel"
                className="form-control"
                placeholder="51929099060"
                value={waPhone}
                onChange={e => setWaPhone(e.target.value)}
                disabled={sendingWA}
                autoFocus
              />
              <p style={{ marginTop: 6, fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                Ejemplo: 51929099060 (Perú) — sin + ni espacios
              </p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowWAModal(false)} disabled={sendingWA}>Cancelar</button>
              <button
                className="btn"
                style={{ background: '#25D366', color: '#fff', border: 'none' }}
                onClick={sendWhatsApp}
                disabled={sendingWA || !waPhone.trim()}
              >
                {sendingWA ? 'Enviando…' : 'Enviar con PDF 📄'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Equipo: dual view tabs */}
      {isEquipo && (
        <div className="eq-view-tabs">
          <button className={`eq-tab ${viewMode === 'internal' ? 'active' : ''}`} onClick={() => setViewMode('internal')}>Vista Interna</button>
          <button className={`eq-tab ${viewMode === 'cliente' ? 'active' : ''}`} onClick={() => setViewMode('cliente')}>Vista Cliente</button>
        </div>
      )}

      {/* Internal profitability dashboard (only for equipo) */}
      {isEquipo && viewMode === 'internal' && equipoData && (
        <div className="card internal-dashboard" style={{ marginBottom: 20 }}>
          <div className="card-header"><h3>Tabla de Rentabilidad</h3><span className="badge badge-enviada" style={{ fontSize: '0.7rem' }}>Confidencial</span></div>
          <table className="eq-internal-tbl">
            <thead><tr><th>Rol</th><th>Cant.</th><th>Dedicación</th><th>Horas</th><th>Costo Int.</th><th>Precio Vta.</th><th>Margen</th></tr></thead>
            <tbody>
              {equipoMembers.map((m, i) => {
                const mg = m.precio_venta_linea > 0 ? ((m.precio_venta_linea - m.costo_total_linea) / m.precio_venta_linea * 100) : 0
                return <tr key={i}><td><strong>{m.rol_comercial}</strong> <span className={`seniority-badge seniority-${m.seniority.toLowerCase().replace('-','')}`}>{m.seniority}</span></td><td>{m.cantidad}</td><td>{fmtFTE(m)}</td><td>{m.horas_mensuales_linea}h</td><td style={{ color: 'var(--danger)' }}>${m.costo_total_linea.toFixed(2)}</td><td>${m.precio_venta_linea.toFixed(2)}</td><td><span className={`margin-indicator ${marginClass(mg)}`}>{mg.toFixed(1)}%</span></td></tr>
              })}
              <tr className="eq-internal-total"><td colSpan={3}><strong>TOTALES</strong></td><td><strong>{equipoTotales?.total_horas_equipo ?? 0}h</strong></td><td style={{ color: 'var(--danger)' }}><strong>${(equipoTotales?.costo_interno_total ?? 0).toFixed(2)}</strong></td><td><strong>${(equipoTotales?.precio_venta_sugerido ?? 0).toFixed(2)}</strong></td><td><span className={`margin-indicator ${marginClass(equipoTotales?.margen_global_proyectado ?? 0)}`} style={{ fontWeight: 700 }}>{(equipoTotales?.margen_global_proyectado ?? 0).toFixed(1)}%</span></td></tr>
            </tbody>
          </table>
          {equipoTotales && equipoTotales.precio_venta_final !== equipoTotales.precio_venta_sugerido && (
            <div style={{ marginTop: 12, padding: 10, background: 'var(--bg-secondary)', borderRadius: 8, fontSize: '0.85rem' }}>
              <strong>Modificador Comercial:</strong> Sugerido ${equipoTotales.precio_venta_sugerido.toFixed(2)} → Final <strong>${equipoTotales.precio_venta_final.toFixed(2)}</strong>
            </div>
          )}
        </div>
      )}

      <div ref={pdfRef} id="cotizacion-pdf-content" className={isBranded ? 'cot-branded' : undefined} style={isEquipo && viewMode === 'internal' ? { display: 'none' } : undefined}>
        {/* PAGE 1+: COVER with auto-pagination for tareas table */}
        {(() => {
          // How many task rows fit on each page (heuristic based on A4 height 1123px)
          const ROWS_FIRST_PAGE = 4   // first page has header+meta+intro taking ~490px, each row ~100-120px
          const ROWS_CONT_PAGE = 6    // continuation pages have minimal chrome ~255px

          // Split tareasItems into page chunks
          const tareasChunks: CotizacionTarea[][] = []
          if (isTareas && tareasItems.length > ROWS_FIRST_PAGE) {
            tareasChunks.push(tareasItems.slice(0, ROWS_FIRST_PAGE))
            let remaining = tareasItems.slice(ROWS_FIRST_PAGE)
            while (remaining.length > 0) {
              tareasChunks.push(remaining.slice(0, ROWS_CONT_PAGE))
              remaining = remaining.slice(ROWS_CONT_PAGE)
            }
          }
          const needsPagination = isTareas && tareasChunks.length > 1

          // Helper to render a task table row
          const renderTareaRow = (t: CotizacionTarea, globalIdx: number) => {
            const tc = t.costos
            const totalMTok = tc ? r2(tc.tokens_input_mtok + tc.tokens_thinking_mtok + tc.tokens_output_mtok) : null
            return (
              <tr key={globalIdx}>
                <td style={{ fontWeight: 700, color: '#9ca3af' }}>{globalIdx + 1}</td>
                <td>
                  <a className="cot-task-link" onClick={() => document.getElementById(`tarea-${globalIdx}`)?.scrollIntoView({ behavior: 'smooth' })}>{t.titulo}</a>
                  {t.descripcion_corta && <div style={{ fontSize: '8pt', color: '#6b7280', fontWeight: 400, marginTop: 2 }}>{t.descripcion_corta}</div>}
                  <a className="cot-task-view-link" onClick={() => document.getElementById(`tarea-${globalIdx}`)?.scrollIntoView({ behavior: 'smooth' })}>Ver detalle →</a>
                </td>
                <td>{t.horas_estimadas ? `${t.horas_estimadas}h` : '—'}</td>
                <td>{totalMTok !== null ? `${totalMTok}M` : '—'}</td>
                <td>{formatCurrency(t.monto, cot.moneda)}</td>
              </tr>
            )
          }

          // Table header for tareas
          const tareasTableHead = (
            <thead><tr><th style={{ width: '6%' }}>#</th><th style={{ width: '42%' }}>Tarea</th><th style={{ width: '14%' }}>Horas</th><th style={{ width: '18%' }}>Tokens</th><th style={{ width: '20%' }}>Monto<br/><span style={{ fontSize: '7pt', fontWeight: 400, color: '#9ca3af' }}>s/IGV</span></th></tr></thead>
          )

          // Determine which items go on the first page
          const firstPageTareas = needsPagination ? tareasChunks[0] : tareasItems

          return (
            <>
              {/* FIRST PAGE: COVER */}
              <div className="cotizacion-preview">
                <div className="cot-header" style={{ background: `linear-gradient(135deg, ${pc} 0%, ${pc}cc 100%)` }}>
                  <div className="cot-header-left">
                    {brand?.logo_url
                      ? <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', display: 'inline-block' }}><img src={brand.logo_url} alt={dispNombre} style={{ height: 26, width: 'auto', display: 'block' }} /></div>
                      : isBranded ? <div className="cot-logo-text">{dispNombre}</div>
                      : empresa.logo_url ? <img src={empresa.logo_url} className="cot-logo" alt={empresa.nombre ?? ''} /> : <div className="cot-logo-text">{dispNombre}</div>}
                  </div>
                  <div className="cot-header-right">
                    {brand?.logo_url_2 ? (
                      <div style={{ background: '#fff', borderRadius: 8, padding: '6px 12px', display: 'inline-block' }}><img src={brand.logo_url_2} alt={brand.nombre_2 || ''} style={{ height: 26, width: 'auto', display: 'block' }} /></div>
                    ) : !isBranded ? (
                      <>
                        <div className="cot-header-fiscal">{empresa.razon_social || ''}</div>
                        <div>RUC {empresa.ruc || ''}</div>
                      </>
                    ) : null}
                  </div>
                </div>

                <div className="cot-title-banner" style={{ borderLeft: `4px solid ${pc}` }}>
                  <div className="cot-title-label">{isEquipo ? 'PROPUESTA DE EQUIPO DEDICADO' : isTareas ? 'PROPUESTA DE SERVICIOS POR TAREA' : 'COTIZACIÓN'}</div>
                  {!isTareas && (
                    <div className="cot-title-amount">
                      {isEquipo ? 'INVERSIÓN MENSUAL' : 'PRESUPUESTO'}:{' '}
                      {cot.descuento_monto > 0 && (
                        <span className="cot-title-amount-old">
                          {brand?.presupuesto_mas_igv
                            ? `${formatCurrency(cot.subtotal, cot.moneda)} + IGV`
                            : formatCurrency(r2(cot.total + cot.descuento_monto), cot.moneda)}
                        </span>
                      )}
                      <strong>
                        {brand?.presupuesto_mas_igv
                          ? `${formatCurrency(r2(cot.subtotal - cot.descuento_monto), cot.moneda)} + IGV`
                          : formatCurrency(cot.total, cot.moneda)}
                        {!brand?.presupuesto_mas_igv && !cot.igv_porcentaje && <span className="cot-sin-igv">sin IGV</span>}
                      </strong>
                    </div>
                  )}
                </div>

                <div className="cot-meta">
                  <div className="cot-meta-block">
                    <h4>Cotizado a</h4>
                    <div className="cot-meta-grid">
                      <span className="cot-meta-key">E:</span><span>{cot.cliente_nombre || '—'}</span>
                      <span className="cot-meta-key">C:</span><span>{cot.cliente_contacto || '—'}</span>
                      <span className="cot-meta-key">T:</span><span>{cot.cliente_telefono || '—'}</span>
                      <span className="cot-meta-key">@:</span><span>{cot.cliente_email || '—'}</span>
                    </div>
                  </div>
                  <div className="cot-meta-block" style={{ textAlign: 'right' }}>
                    <h4>Detalles</h4>
                    <div className="cot-meta-grid" style={{ justifyItems: 'end' }}>
                      <span className="cot-meta-key">N:</span><span>{cot.numero}</span>
                      <span className="cot-meta-key">F:</span><span>{formatDate(cot.fecha_emision)}</span>
                      <span className="cot-meta-key">D:</span><span>Validez: {cot.validez_dias} días calendario</span>
                    </div>
                  </div>
                </div>

                <div className="cot-body">
                  <p className="cot-intro">
                    {isEquipo
                      ? <>A través del presente documento, ponemos a su disposición la propuesta de <strong>{proyectoNombre}</strong>. A continuación, se detalla la composición del equipo profesional dedicado y la inversión mensual asociada.</>
                      : isTareas
                      ? <>A través del presente documento, ponemos a su disposición la propuesta económica correspondiente. En las siguientes secciones encontrará el desglose detallado de cada requerimiento y la inversión asociada.</>
                      : <>A través del presente documento, ponemos a su disposición la propuesta económica correspondiente al <strong>{proyectoNombre}</strong>. En la siguiente sección encontrará el desglose detallado de la inversión.</>
                    }
                  </p>

                  {isEquipo ? (
                    <>
                      <h3 className="cot-section-title" style={{ borderColor: pc }}>Composición del Equipo</h3>
                      <table className="cot-table">
                        <thead><tr><th style={{ width: '40%' }}>Rol Profesional</th><th style={{ width: '15%' }}>Cantidad</th><th style={{ width: '25%' }}>Dedicación</th><th style={{ width: '20%' }}>Horas / Mes</th></tr></thead>
                        <tbody>
                          {equipoMembers.map((m, i) => <tr key={i}><td><strong>{m.rol_comercial}</strong> ({m.seniority})</td><td>{m.cantidad}</td><td>{fmtFTE(m)}</td><td>{m.horas_mensuales_linea}h</td></tr>)}
                          <tr style={{ background: 'var(--bg-secondary)', fontWeight: 700 }}><td colSpan={3}>Total Horas del Equipo</td><td>{equipoTotales?.total_horas_equipo ?? 0}h</td></tr>
                        </tbody>
                      </table>
                    </>
                  ) : isTareas ? (
                    <div className="cot-tareas-summary" id="resumen">
                      <h3 className="cot-section-title" style={{ borderColor: pc }}>Desglose de Tareas</h3>
                      <table className="cot-table">
                        {tareasTableHead}
                        <tbody>
                          {firstPageTareas.map((t, i) => renderTareaRow(t, i))}
                        </tbody>
                      </table>
                      {/* Only show note if all rows fit on first page */}
                      {!needsPagination && secciones.length > 0 && (
                        <p className="cot-note">La tabla anterior representa un resumen económico. Para conocer las especificaciones técnicas, cronogramas, entregables y el desglose detallado, <strong>le invitamos a revisar las páginas siguientes</strong>.</p>
                      )}
                    </div>
                  ) : (
                    <table className="cot-table">
                      <thead><tr><th style={{ width: '50%' }}>Descripción</th><th style={{ width: '18%' }}>Costo</th><th style={{ width: '10%' }}>Cant.</th><th style={{ width: '22%' }}>Total</th></tr></thead>
                      <tbody>{items.map((it, i) => (
                        <tr key={i}><td>{it.descripcion}</td><td>{formatCurrency(it.precio_unitario, cot.moneda)}</td><td>{it.cantidad}</td><td>{formatCurrency(it.total || it.precio_unitario * it.cantidad, cot.moneda)}</td></tr>
                      ))}</tbody>
                    </table>
                  )}

                  {!isTareas && (
                    <>
                      <div className="cot-bottom-row">
                        <DatosBancarios empresa={empresa} moneda={cot.moneda} />
                        <div className="cot-totals-box">
                          {/* Sin IGV el subtotal repetiria el total: solo se muestra si hay impuesto que sumar o descuento que explicar. */}
                          {(cot.igv_porcentaje || cot.descuento_monto) ? (
                            <div className="cot-totals-row"><span>Subtotal</span><span>{formatCurrency(cot.subtotal, cot.moneda)}</span></div>
                          ) : null}
                          {cot.igv_porcentaje ? (
                            <div className="cot-totals-row"><span>IGV ({cot.igv_porcentaje}%)</span><span>{formatCurrency(cot.igv_monto, cot.moneda)}</span></div>
                          ) : null}
                          {tier.label !== 'Sin descuento' ? (
                            <div className="cot-totals-row cot-discount-row">
                              <span className="cot-discount-label"><span className="cot-discount-badge" style={{ background: tier.color }}>{tier.emoji} {tier.label}</span><span className="cot-discount-pct">-{fmtPct(cot.descuento_porcentaje)}%</span></span>
                              <span>-{formatCurrency(cot.descuento_monto, cot.moneda)}</span>
                            </div>
                          ) : cot.igv_porcentaje ? (
                            <div className="cot-totals-row"><span>Descuento 0%</span><span>{formatCurrency(0, cot.moneda)}</span></div>
                          ) : null}
                          <div className="cot-totals-row total" style={{ background: pc }}><span>TOTAL GENERAL{!cot.igv_porcentaje && <span className="cot-sin-igv">sin IGV</span>}</span><span>{formatCurrency(cot.total, cot.moneda)}</span></div>
                        </div>
                      </div>
                      {secciones.length > 0 && (
                        <p className="cot-note">La tabla anterior representa un resumen económico. Para conocer las especificaciones técnicas, cronogramas, entregables y el desglose detallado, <strong>le invitamos a revisar las páginas siguientes</strong>.</p>
                      )}
                    </>
                  )}
                </div>

                <div className="cot-footer" style={{ background: pc }}>
                  <span>{dispNombre}</span>
                  <span>{isBranded ? '' : `${empresa.ubicacion || ''} | ${empresa.telefono || ''}`}</span>
                  <span>{dispWebsite}</span>
                </div>
              </div>

              {/* CONTINUATION PAGES: remaining task rows + bank info merged when space allows */}
              {needsPagination && tareasChunks.slice(1).map((chunk, chunkIdx) => {
                const globalOffset = ROWS_FIRST_PAGE + chunkIdx * ROWS_CONT_PAGE
                const isLastChunk = chunkIdx === tareasChunks.length - 2
                // If last chunk has ≤4 rows (~400px), merge bank info here (~350px fits)
                const mergeBankInfo = isLastChunk && chunk.length <= 4
                return (
                  <div key={`cover-cont-${chunkIdx}`} className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
                    <div className="cot-page-header" style={{ background: pc }} />
                    <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
                    <div className="cot-body" style={{ paddingTop: 40 }}>
                      <h3 className="cot-section-title" style={{ borderColor: pc }}>Desglose de Tareas (cont.)</h3>
                      <table className="cot-table">
                        {tareasTableHead}
                        <tbody>
                          {chunk.map((t, i) => renderTareaRow(t, globalOffset + i))}
                        </tbody>
                      </table>
                      {isLastChunk && secciones.length > 0 && (
                        <p className="cot-note">La tabla anterior representa un resumen económico. Para conocer las especificaciones técnicas, cronogramas, entregables y el desglose detallado, <strong>le invitamos a revisar las páginas siguientes</strong>.</p>
                      )}
                      {mergeBankInfo && (
                        <>
                          <h3 className="cot-section-title" style={{ borderColor: pc, marginTop: 24 }}>Datos de Pago</h3>
                          <div className="cot-bottom-row">
                            <DatosBancarios empresa={empresa} moneda={cot.moneda} />
                          </div>
                          <div className="cot-validez">Esta cotización tiene una validez de {cot.validez_dias} días calendario a partir de la fecha de emisión.</div>
                        </>
                      )}
                    </div>
                    <div className="cot-footer" style={{ background: pc }}>
                      <span>{dispNombre}</span>
                      <span>Pág. {chunkIdx + 2}</span>
                      <span>{dispWebsite}</span>
                    </div>
                  </div>
                )
              })}
            </>
          )
        })()}

        {/* PAGE 1.5: BANK INFO (tareas only — separate page ONLY if not already merged into last continuation page) */}
        {isTareas && (() => {
          const ROWS_FIRST_PAGE = 4
          const ROWS_CONT_PAGE = 6
          const totalRows = (((cot.items && typeof cot.items === 'object' && 'tareas' in cot.items) ? (cot.items as CotizacionItemsTareas).tareas : []) as CotizacionTarea[]).length
          // Bank info was merged if pagination happened AND last chunk ≤4 rows
          const needsPagination = totalRows > ROWS_FIRST_PAGE
          const lastChunkSize = needsPagination ? ((totalRows - ROWS_FIRST_PAGE) % ROWS_CONT_PAGE || ROWS_CONT_PAGE) : 0
          const wasMerged = needsPagination && lastChunkSize <= 4
          if (wasMerged) return null
          return (
          <div className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
            <div className="cot-page-header" style={{ background: pc }} />
            <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
            <div className="cot-body" style={{ paddingTop: 40 }}>
              <h3 className="cot-section-title" style={{ borderColor: pc }}>Datos de Pago</h3>
              <div className="cot-bottom-row">
                <DatosBancarios empresa={empresa} moneda={cot.moneda} />
              </div>
              <div className="cot-validez">Esta cotización tiene una validez de {cot.validez_dias} días calendario a partir de la fecha de emisión.</div>
            </div>
            <div className="cot-footer" style={{ background: pc }}>
              <span>{dispNombre}</span>
              <span>Pág. 2</span>
              <span>{dispWebsite}</span>
            </div>
          </div>
          )
        })()}

        {/* PAGE 2: INDEX (if sections exist) */}
        {secciones.length > 0 && (
          <div className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
            <div className="cot-page-header" style={{ background: pc }} />
            <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
            <div className="cot-body" style={{ paddingTop: 40 }}>
              <h2 className="cot-page-title">Cotización de Servicios</h2>
              <div className="cot-index-meta">
                <p>Fecha de Emisión: <em>{formatDate(cot.fecha_emision)}</em></p>
                <p>Proyecto/Servicio: <em>{proyectoNombre}</em></p>
              </div>
              <div className="cot-index-box">
                <h3>Índice</h3>
                {secciones.map((s, i) => (
                  <div key={i} className="cot-index-item"><strong>{s.titulo}</strong><span className="cot-index-dots" /><span className="cot-index-page">{i + 3}</span></div>
                ))}
              </div>
            </div>
            <div className="cot-footer" style={{ background: pc }}><span>{dispNombre}</span><span>Pág. 2</span><span>{dispWebsite}</span></div>
          </div>
        )}

        {/* SECTION PAGES */}
        {secciones.map((sec, idx) => (
          <div key={idx} className="cotizacion-preview cot-page" style={{ marginTop: 24 }}>
            <div className="cot-page-header" style={{ background: pc }} />
            <div className="cot-page-ruc">{cot.cliente_ruc || ''}</div>
            <div className="cot-body">
              <h3 className="cot-section-title" style={{ borderColor: pc }}>{sec.titulo}</h3>
              <div className="cot-section-content" dangerouslySetInnerHTML={{ __html: expandirBloques(sanitizeHtml(processContent(sec.contenido)), cot) }} />
            </div>
            <div className="cot-footer" style={{ background: pc }}><span>{dispNombre}</span><span>Pág. {idx + 3}</span><span>{dispWebsite}</span></div>
          </div>
        ))}

        {/* TASK DETAIL PAGES (only for tipo tareas) — auto-paginated by h4 sections */}
        {isTareas && tareasItems.map((tarea, idx) => {
          // Base page offset: cover(1) + bank(1) + index?(secciones.length>0 ? 1 : 0) + sections
          const baseOffset = 2 + (secciones.length > 0 ? 1 + secciones.length : 0)
          // Sum pages used by previous tasks (estimate: each prior task gets ~2-4 pages)
          const startPage = baseOffset + idx * 3 + 1
          return (
            <TaskDetailPages
              key={`tarea-${idx}`}
              tarea={tarea}
              idx={idx}
              totalTareas={tareasItems.length}
              pc={pc}
              moneda={cot.moneda}
              empresaNombre={dispNombre}
              empresaWebsite={dispWebsite}
              clienteRuc={cot.cliente_ruc || ''}
              startPage={startPage}
              formatCurrency={formatCurrency}
              processContent={processContent}
            />
          )
        })}
      </div>
    </div>
  )
}
